import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { routing } from './app-routing.module';
import { NgModule, LOCALE_ID, APP_INITIALIZER } from '@angular/core';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatNativeDateModule, MAT_DATE_LOCALE, MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule as MatFormFieldModule, MAT_LEGACY_FORM_FIELD_DEFAULT_OPTIONS as MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/legacy-form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyProgressBarModule as MatProgressBarModule } from '@angular/material/legacy-progress-bar';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MatLegacySelectModule as MatSelectModule, MAT_LEGACY_SELECT_CONFIG as MAT_SELECT_CONFIG } from '@angular/material/legacy-select';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatStepperModule } from '@angular/material/stepper';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatMomentDateModule, MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { FlexLayoutModule } from '@angular/flex-layout';
import { AppComponent } from './app.component';
import { CalculateKfzComponent } from './components/calculatekfz/calculatekfz.component';
import { ErrorComponent } from './components/error/error.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { PopupComponent } from './components/popup/popup.component';
import { LoadingStatus, LoadingInterceptor } from '../shared/loading';
import { RisikoKfzComponent } from './components/calculatekfz/risikokfz/risikokfz.component';
import { KaskoKfzComponent } from './components/calculatekfz/kaskokfz/kaskokfz.component';
import { PropagateSubmitDirective } from './directives/propagatesubmit.directive';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CalculateRsComponent } from './components/calculaters/calculaters.component';
import { NgxMaskModule, IConfig } from 'ngx-mask';

import { DatePipe, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { HeaderComponent } from './components/header/header.component';
import { OffertComponent } from './components/offert/offert.component';
import { AntragComponent } from './components/antrag/antrag.component';
import { KfzAntragComponent } from './components/antrag/kfz-antrag/kfz-antrag.component';
import { AngularIbanModule } from 'angular-iban';
import { ViewpdfComponent } from './components/viewpdf/viewpdf.component';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { ZahlungsartHvComponent } from './components/calculatehv/zahlungsart-hv/zahlungsart-hv.component';
import { PraemienStatus } from '../shared/praemie';
import { BlockcopypasteDirective } from './directives/blockcopypaste.directive';
import { RsAntragComponent } from './components/antrag/rs-antrag/rs-antrag.component';
import { AntragWeitereFragenComponent } from './components/antrag/antrag-weitere-fragen/antrag-weitere-fragen.component';
import { ZusammenfassungComponent } from './components/zusammenfassung/zusammenfassung.component';
import { RsZusammenfassungComponent } from './components/zusammenfassung/rs-zusammenfassung/rs-zusammenfassung.component';
import { SelectionChangeScrollInvalidControlDirective } from './directives/selection-change-scroll-invalid-control.directive';
import { SubmitScrollInvalidControlDirective } from './directives/submit-scroll-invalid-control.directive';
import { CustomCurrencyPipe } from './pipes/custom-currency.pipe';
import { AdresseComponent } from './components/adresse/adresse.component';
import { StepperScrollControlDirective } from './directives/stepper-scroll-control.directive';
import { ScrollControl } from '../shared/stepper';
import { VereinbarungKommunikationComponent } from './components/vereinbarung-kommunikation/vereinbarung-kommunikation.component';
import { HaftpflichtHundeComponent as HaftpflichtHundeComponentLegacy } from './components/haftpflicht-hunde/legacy/haftpflicht-hunde.component';
import { HaftpflichtPersonenComponent as HaftpflichtPersonenComponentLegacy } from './components/haftpflicht-personen/legacy/haftpflicht-personen.component';
import { VersicherungsbestaetigungComponent } from './components/versicherungsbestaetigung/versicherungsbestaetigung.component';
import { DatenschutzPolicyComponent } from './components/datenschutz-policy/datenschutz-policy.component';
import { PartnerComponent } from './components/partner/partner.component';
import { CurrentRechner } from '../shared/rechner';
import { MonthYearDateFormatDirective } from './directives/month-year-date-format.directive';
import { DatenBeratungsprotokollComponent } from './components/daten-beratungsprotokoll/daten-beratungsprotokoll.component';
import { KfzZusammenfassungComponent } from './components/zusammenfassung/kfz-zusammenfassung/kfz-zusammenfassung.component';
import { VorversicherungComponent } from './components/vorversicherung/vorversicherung.component';
import { MatSortModule } from '@angular/material/sort';
import { MatLegacyPaginatorIntl as MatPaginatorIntl, MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { MatPaginatorIntlCustom } from '../shared/paginator';
import { VersicherungsnehmerListDialogComponent } from './components/partner/partner-list-dialog/partner-list-dialog.component';
import { OffertVerificationDialogComponent } from './components/offert/offert-verification-dialog/offert-verification-dialog.component';
import { VerbauteFlaecheComponent } from './components/verbaute-flaeche/verbaute-flaeche.component';
import { VertragsbeginnLaufzeitComponent } from './components/vertragsbeginn-laufzeit/vertragsbeginn-laufzeit.component';
import { BewilligungsjahrHausComponent } from './components/bewilligungsjahr-haus/bewilligungsjahr-haus.component';
import { GebaeudeHochwasserzoneComponent } from './components/gebaeude-hochwasserzone/gebaeude-hochwasserzone.component';
import { NebengebaeudeComponent } from './components/nebengebaeude/nebengebaeude.component';
import { CalculatehvComponent } from './components/calculatehv/calculatehv.component';
import { VariantenHvComponent } from './components/calculatehv/varianten-hv/varianten-hv.component';
import { CalculateHvDatenComponent } from './components/calculatehv/calculatehv-daten/calculatehv-daten.component';
import { CalculateHvErweiterungenComponent } from './components/calculatehv/calculatehv-erweiterungen/calculatehv-erweiterungen.component';
import { ErhoehungHoechsthaftungssummeComponent } from './components/erhoehung-hoechsthaftungssumme/erhoehung-hoechsthaftungssumme.component';
import { HaftpflichtHundeComponent } from './components/haftpflicht-hunde/haftpflicht-hunde.component';
import { HaftpflichtPersonenComponent } from './components/haftpflicht-personen/haftpflicht-personen.component';
import { AufbewahrungWertsachenComponent } from './components/aufbewahrung-wertsachen/aufbewahrung-wertsachen.component';
import { CalculateHvZusatzpaketeComponent } from './components/calculatehv/calculatehv-zusatzpakete/calculatehv-zusatzpakete.component';
import { HvPaketComponent } from './components/calculatehv/calculatehv-zusatzpakete/hv-paket/hv-paket.component';
import { SettingsService } from './service/settings.service';
import { HvAntragComponent } from './components/antrag/hv-antrag/hv-antrag.component';
import { GebaeudetypDachComponent } from './components/calculatehv/gebaeudetyp-dach/gebaeudetyp-dach.component';
import { HvZusammenfassungComponent } from './components/zusammenfassung/hv-zusammenfassung/hv-zusammenfassung.component';
import { StepperDisableFutureStepsDirective } from './directives/stepper-disable-future-steps.directive';
import { EurotaxListDialogComponent } from './components/calculatekfz/eurotax-list-dialog/eurotax-list-dialog.component';
import { AppLabelComponent } from './components/app-label/app-label.component';
import { MaintainanceComponent } from './components/maintainance/maintainance.component';
import { AppTextareaComponent } from './components/app-textarea/app-textarea.component';
import { ProcessingStatus } from '../shared/processing';
import { PreventClickProcessingDirective } from './directives/prevent-click-processing.directive';
import { UserStatusService } from './service/user.status.service';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { DragDropDirective } from './directives/drag-drop.directive';
import { FertigstellenComponent } from './components/fertigstellen/fertigstellen.component';
import { KfzFertigstellenComponent } from './components/fertigstellen/kfz-fertigstellen/kfz-fertigstellen.component';
import { StepperLinearInitFutureStepDirective } from './directives/stepper-linear-init-future-step.directive';
import { StepperDisableStepsDirective } from './directives/stepper-disable-steps.directive';
import { BoolToJaNeinPipe } from './pipes/bool-to-ja-nein.pipe';
import { ToCapLetterPipe } from './pipes/to-cap-letter.pipe';
import { StepperDisableStepDirective } from './directives/stepper-disable-step.directive';
import { TimestampToStringPipe } from './pipes/timestamp-to-string.pipe';
import { NgxPiwikProModule, NgxPiwikProRouterModule } from '@piwikpro/ngx-piwik-pro';
import { GearsAnimationStatus } from '../shared/gearsanimation';
import { UserType } from '../shared/enum';
import { FriendlycaptchaWidgetComponent } from './components/friendlycaptcha-widget/friendlycaptcha-widget.component';
import { VermittlerService } from './service/vermittler.service';
import { BerechnungTableComponent } from './components/calculatekfz/berechnung-table/berechnung-table.component';
import { environment } from '../environments/environment';

registerLocaleData(localeDe, 'de');
export const MY_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD.MM.YYYY',
    monthYearA11yLabel: 'MMM YYYY',
  },
};
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export const options: Partial<IConfig> | (() => Partial<IConfig>) = null;

function settingsLoadFactory(settingsService: SettingsService) {
  return () =>
    settingsService.loadSettings().then(() => settingsService);
}

function userStatusLoadFactory(userStatusService: UserStatusService) {
  return () =>
    userStatusService.loadUserStatus().then(() => userStatusService);
}

function vermittlerResultLoadFactory(vermittlerService: VermittlerService) {
  return () =>
    vermittlerService.loadVermittlerResult().then(() => vermittlerService);
}

function initializeApp(userStatusService: UserStatusService, settingsService: SettingsService) {
  return (): Promise<void> => {
    return new Promise(resolve => {
      userStatusService.loadUserStatus().then(() => {
        const userType = userStatusService.userStatus.UserType;
        if (window.location.href.indexOf('expert') >= 0 &&
          userType !== UserType.Mitarbeiter &&
          userType !== UserType.Vermittler) {
          settingsService.loadSettings().then(() => {
            window.location.href = settingsService.settings.VppUrl;
          });
        } else {
          resolve();
        }
      });
    });
  };
}

@NgModule({
  declarations: [
    AppComponent,
    CalculateKfzComponent,
    PopupComponent,
    RisikoKfzComponent,
    KaskoKfzComponent,
    PropagateSubmitDirective,
    BlockcopypasteDirective,
    ErrorComponent,
    HeaderComponent,
    CalculateRsComponent,
    OffertComponent,
    AntragComponent,
    KfzAntragComponent,
    ViewpdfComponent,
    ZahlungsartHvComponent,
    RsAntragComponent,
    AntragWeitereFragenComponent,
    ZusammenfassungComponent,
    RsZusammenfassungComponent,
    SelectionChangeScrollInvalidControlDirective,
    SubmitScrollInvalidControlDirective,
    CustomCurrencyPipe,
    AdresseComponent,
    StepperScrollControlDirective,
    StepperDisableFutureStepsDirective,
    StepperDisableStepsDirective,
    StepperDisableStepDirective,
    StepperLinearInitFutureStepDirective,
    VereinbarungKommunikationComponent,
    HaftpflichtHundeComponentLegacy,
    HaftpflichtPersonenComponentLegacy,
    VersicherungsbestaetigungComponent,
    DatenschutzPolicyComponent,
    PartnerComponent,
    MonthYearDateFormatDirective,
    DatenBeratungsprotokollComponent,
    KfzZusammenfassungComponent,
    VorversicherungComponent,
    VersicherungsnehmerListDialogComponent,
    OffertVerificationDialogComponent,
    VerbauteFlaecheComponent,
    VertragsbeginnLaufzeitComponent,
    CalculatehvComponent,
    VariantenHvComponent,
    BewilligungsjahrHausComponent,
    GebaeudeHochwasserzoneComponent,
    NebengebaeudeComponent,
    CalculateHvDatenComponent,
    CalculateHvErweiterungenComponent,
    HaftpflichtHundeComponent,
    HaftpflichtPersonenComponent,
    ErhoehungHoechsthaftungssummeComponent,
    AufbewahrungWertsachenComponent,
    CalculateHvZusatzpaketeComponent,
    HvPaketComponent,
    HvAntragComponent,
    GebaeudetypDachComponent,
    HvZusammenfassungComponent,
    EurotaxListDialogComponent,
    AppLabelComponent,
    MaintainanceComponent,
    AppTextareaComponent,
    PreventClickProcessingDirective,
    FileUploadComponent,
    DragDropDirective,
    FertigstellenComponent,
    KfzFertigstellenComponent,
    BoolToJaNeinPipe,
    ToCapLetterPipe,
    TimestampToStringPipe,
    FriendlycaptchaWidgetComponent,
    BerechnungTableComponent
  ],
  imports: [
    BrowserModule,
    RouterModule,
    routing,
    BrowserAnimationsModule,
    FlexLayoutModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatMomentDateModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatGridListModule,
    MatCardModule,
    MatCheckboxModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatIconModule,
    MatDialogModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatToolbarModule,
    MatStepperModule,
    MatRadioModule,
    MatListModule,
    MatExpansionModule,
    HttpClientModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatSlideToggleModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    AngularIbanModule,
    PdfJsViewerModule,
    NgxMaskModule.forRoot(),
    NgxPiwikProModule.forRoot(environment.piwikSiteKey, 'https://hdi.piwik.pro'),
    NgxPiwikProRouterModule
  ],
  providers: [
    LoadingStatus,
    PraemienStatus,
    CurrentRechner,
    ProcessingStatus,
    ScrollControl,
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: MAT_SELECT_CONFIG, useValue: { disableOptionCentering: true } },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'fill' } },
    CustomCurrencyPipe,
    BoolToJaNeinPipe,
    { provide: MatPaginatorIntl, useClass: MatPaginatorIntlCustom },
    DatePipe,
    {
      provide: APP_INITIALIZER,
      useFactory: settingsLoadFactory,
      deps: [SettingsService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: userStatusLoadFactory,
      deps: [UserStatusService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: vermittlerResultLoadFactory,
      deps: [VermittlerService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [UserStatusService, SettingsService],
      multi: true
    },
    GearsAnimationStatus
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
